import { useState, useEffect } from "react";
import { Settings } from "lucide-react";
import { Card, Button, Seo } from "../../components";
import axios from "axios";
import { PROFILE_URL, CHANGE_PASSWORD_URL } from "../../features/UrlHelper";
import { handleApiError } from "../../features/errorHandler";
import {
  EMPTY_ADDRESS,
  POSTAL_CODE_PATTERN,
  POSTAL_CODE_TITLE,
  STREET_PATTERN,
  STREET_TITLE,
  isAddressComplete,
} from "../../features/address";
import { PHONE_PATTERN, PHONE_TITLE } from "../../features/phone";
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_MIN_LENGTH_MESSAGE,
} from "../../features/password";
import "./ProfilePage.scss";

const EMPTY_CONTACT = { ...EMPTY_ADDRESS, phone: "" };

const authHeaders = () => ({
  headers: {
    Authorization: `Bearer ${window.localStorage.getItem("authToken")}`,
  },
});

function SettingsSection() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [profileLoading, setProfileLoading] = useState(true);

  const [contact, setContact] = useState(EMPTY_CONTACT);
  const [savedContact, setSavedContact] = useState(EMPTY_CONTACT);
  const [contactSaving, setContactSaving] = useState(false);
  const [contactMessage, setContactMessage] = useState("");
  const [contactError, setContactError] = useState("");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");

  useEffect(() => {
    axios
      .get(PROFILE_URL, authHeaders())
      .then((res) => {
        setFirstName(res.data.firstName || "");
        setLastName(res.data.lastName || "");
        setEmail(res.data.email || "");
        const loadedContact = {
          street: res.data.street || "",
          postalCode: res.data.postalCode || "",
          city: res.data.city || "",
          phone: res.data.phone || "",
        };
        setContact(loadedContact);
        setSavedContact(loadedContact);
      })
      .catch((err) => handleApiError(err, "Nepodarilo sa načítať profil."))
      .finally(() => setProfileLoading(false));
  }, []);

  const updateContactField = (key) => (e) => {
    const value = e.target.value;
    setContact((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveContact = (e) => {
    e.preventDefault();
    setContactError("");
    setContactMessage("");
    setContactSaving(true);
    axios
      .post(PROFILE_URL, contact, authHeaders())
      .then((res) => {
        const saved = {
          street: res.data.street,
          postalCode: res.data.postalCode,
          city: res.data.city,
          phone: res.data.phone,
        };
        setContact(saved);
        setSavedContact(saved);
        setContactMessage(res.data.message || "Kontaktné údaje boli uložené.");
      })
      .catch((err) => {
        setContactError(
          handleApiError(err, "Nepodarilo sa uložiť kontaktné údaje.")
        );
      })
      .finally(() => setContactSaving(false));
  };

  const handleChangePassword = (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordMessage("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("Všetky polia sú povinné.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Nové heslá sa nezhodujú.");
      return;
    }
    if (newPassword.length < PASSWORD_MIN_LENGTH) {
      setPasswordError(PASSWORD_MIN_LENGTH_MESSAGE);
      return;
    }

    setPasswordChanging(true);
    axios
      .post(
        CHANGE_PASSWORD_URL,
        { currentPassword, newPassword, newPasswordConfirm: confirmPassword },
        authHeaders()
      )
      .then((res) => {
        setPasswordMessage(res.data.message || "Heslo bolo úspešne zmenené.");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      })
      .catch((err) => {
        const msg = handleApiError(err, "Chyba pri zmene hesla.");
        setPasswordError(msg);
      })
      .finally(() => setPasswordChanging(false));
  };

  if (profileLoading) {
    return (
      <div className="profile__section">
        <h2 className="profile__section-title">Nastavenia profilu</h2>
        <p>Načítavam...</p>
      </div>
    );
  }

  return (
    <div className="profile__section">
      <Seo title="Profil" noIndex={true} />
      <h2 className="profile__section-title">Nastavenia profilu</h2>

      <Card>
        <Card.Header>
          <Card.Title>Osobné údaje</Card.Title>
        </Card.Header>
        <Card.Body>
          <div className="profile__form">
            <div className="profile__form-row">
              <div className="profile__form-field">
                <label className="profile__form-label">Meno</label>
                <input
                  className="profile__form-input"
                  value={firstName}
                  disabled
                />
              </div>
              <div className="profile__form-field">
                <label className="profile__form-label">Priezvisko</label>
                <input
                  className="profile__form-input"
                  value={lastName}
                  disabled
                />
              </div>
            </div>
            <div className="profile__form-field">
              <label className="profile__form-label">Email</label>
              <input className="profile__form-input" value={email} disabled />
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <Card.Title>Kontaktné údaje</Card.Title>
        </Card.Header>
        <Card.Body>
          <form className="profile__form" onSubmit={handleSaveContact}>
            {!isAddressComplete(savedContact) && (
              <p className="profile__form-error">
                Adresa je povinná – bez nej nie je možné zakúpiť členstvo,
                pretože sa uvádza na faktúre.
              </p>
            )}
            {!savedContact.phone && (
              <p className="profile__form-error">
                Telefónne číslo je povinné – doplňte ho prosím.
              </p>
            )}
            <div className="profile__form-field">
              <label className="profile__form-label" htmlFor="profile-phone">
                Telefónne číslo
              </label>
              <input
                id="profile-phone"
                type="tel"
                className="profile__form-input"
                value={contact.phone}
                onChange={updateContactField("phone")}
                placeholder="0905 123 456"
                maxLength={20}
                pattern={PHONE_PATTERN}
                title={PHONE_TITLE}
                required
                autoComplete="tel"
              />
            </div>
            <div className="profile__form-field">
              <label className="profile__form-label" htmlFor="profile-street">
                Ulica a číslo domu
              </label>
              <input
                id="profile-street"
                className="profile__form-input"
                value={contact.street}
                onChange={updateContactField("street")}
                placeholder="Hlavná 12"
                maxLength={255}
                pattern={STREET_PATTERN}
                title={STREET_TITLE}
                required
                autoComplete="street-address"
              />
            </div>
            <div className="profile__form-row">
              <div className="profile__form-field">
                <label className="profile__form-label" htmlFor="profile-postal-code">
                  PSČ
                </label>
                <input
                  id="profile-postal-code"
                  className="profile__form-input"
                  value={contact.postalCode}
                  onChange={updateContactField("postalCode")}
                  placeholder="831 06"
                  inputMode="numeric"
                  maxLength={6}
                  pattern={POSTAL_CODE_PATTERN}
                  title={POSTAL_CODE_TITLE}
                  required
                  autoComplete="postal-code"
                />
              </div>
              <div className="profile__form-field">
                <label className="profile__form-label" htmlFor="profile-city">
                  Mesto
                </label>
                <input
                  id="profile-city"
                  className="profile__form-input"
                  value={contact.city}
                  onChange={updateContactField("city")}
                  placeholder="Bratislava"
                  maxLength={100}
                  required
                  autoComplete="address-level2"
                />
              </div>
            </div>
            {contactError && (
              <p className="profile__form-error">{contactError}</p>
            )}
            {contactMessage && (
              <p className="profile__form-success">{contactMessage}</p>
            )}
            <div className="profile__form-actions">
              <Button type="submit" variant="primary" disabled={contactSaving}>
                {contactSaving ? "Ukladám..." : "Uložiť údaje"}
              </Button>
            </div>
          </form>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <Card.Title>Zmena hesla</Card.Title>
        </Card.Header>
        <Card.Body>
          <form className="profile__form" onSubmit={handleChangePassword}>
            <input
              type="email"
              name="email"
              value={email}
              autoComplete="username"
              readOnly
              hidden
            />
            <div className="profile__form-field">
              <label className="profile__form-label">Aktuálne heslo</label>
              <input
                type="password"
                name="current-password"
                className="profile__form-input"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Zadajte aktuálne heslo"
                autoComplete="current-password"
              />
            </div>
            <div className="profile__form-row">
              <div className="profile__form-field">
                <label className="profile__form-label">Nové heslo</label>
                <input
                  type="password"
                  name="new-password"
                  className="profile__form-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={`Min. ${PASSWORD_MIN_LENGTH} znakov`}
                  autoComplete="new-password"
                />
              </div>
              <div className="profile__form-field">
                <label className="profile__form-label">Potvrdenie hesla</label>
                <input
                  type="password"
                  name="confirm-password"
                  className="profile__form-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Zopakujte nové heslo"
                  autoComplete="new-password"
                />
              </div>
            </div>
            {passwordError && (
              <p className="profile__form-error">{passwordError}</p>
            )}
            {passwordMessage && (
              <p className="profile__form-success">{passwordMessage}</p>
            )}
            <div className="profile__form-actions">
              <Button
                type="submit"
                variant="primary"
                disabled={passwordChanging}
              >
                {passwordChanging ? "Mením heslo..." : "Zmeniť heslo"}
              </Button>
            </div>
          </form>
        </Card.Body>
      </Card>
    </div>
  );
}

// ─── Main component ──────────────────────────────

function ProfilePage() {
  return (
    <div className="profile">
      <main className="profile__content">
        <SettingsSection />
      </main>
    </div>
  );
}

export default ProfilePage;
